import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";
import { middlewares } from "./middlewares";
import {
  CHECKOUT_STEPS,
  PAYMENT_METHOD_OPTIONS,
  TRANSLATION_OPTIONS,
} from "@/components/screen/start-order/components/Checkout/constants";
import {
  deliverType,
  translationType,
} from "@/components/screen/start-order/components/Checkout";
import { PickerFileMetadata } from "@/components/ui/file-stack-uploader";

export type CheckoutStepOne = {
  translationType?: translationType;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type CheckoutStepTwo = {
  translationType?: translationType;
  fromLanguage?: string;
  toLanguage?: string;
  files?: PickerFileMetadata[] | null;
};

export type CheckoutStepThree = {
  delivery?: deliverType[];
  projectDescription?: string;
  recipient?: string;
  streetAddress?: string;
  city?: string;
  zip?: string;
  state?: string;
  country?: string;
};

export type CheckoutStepFour = {
  method?: string;
  number?: string;
  expiry?: string;
  name?: string;
  securityCode?: string;
  accountMode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type CheckoutStoreState = {
  stepOne: CheckoutStepOne;
  stepTwo: CheckoutStepTwo;
  stepThree: CheckoutStepThree;
  stepFour: CheckoutStepFour;
  currentStep: number;
  maxReachedStep: number;
  checkoutData: Record<string, unknown> | null;
};

type CheckoutStoreActions = {
  setField: <T extends keyof CheckoutStoreState>(
    field: T,
    value: CheckoutStoreState[T]
  ) => void;
  setFields: (fields: Partial<CheckoutStoreState>) => void;
  cleanUp: () => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  goToStep: (step: number) => void;
  setCheckoutData: (data: Record<string, unknown>) => void;
  clearCheckoutData: () => void;
  validateCurrentStep: () => Promise<boolean>;
  setStepValidator: (step: number, validator: () => Promise<boolean>) => void;
};

type CheckoutStore = CheckoutStoreState & CheckoutStoreActions;

const initialState: CheckoutStoreState = {
  stepOne: {
    translationType: TRANSLATION_OPTIONS.standard,
    firstName: "",
    lastName: "",
    email: "",
  },
  stepTwo: {
    translationType: TRANSLATION_OPTIONS.standard,
    fromLanguage: "English",
    toLanguage: "",
    files: null,
  },
  stepThree: {
    delivery: [],
    projectDescription: "",
    recipient: "",
    streetAddress: "",
    city: "",
    zip: "",
    state: "",
    country: "",
  },
  stepFour: {
    method: PAYMENT_METHOD_OPTIONS.card,
    number: "",
    expiry: "",
    name: "",
    securityCode: "",
    accountMode: "",
    firstName: "",
    lastName: "",
    email: "",
  },
  currentStep: 1,
  maxReachedStep: 1,
  checkoutData: null,
};

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
};

const stepValidators = new Map<number, () => Promise<boolean>>();

const createStore: StateCreator<
  CheckoutStore,
  [["zustand/devtools", never], ["zustand/immer", never]],
  [["zustand/persist", unknown]]
> = (set, get) => {
  return {
    ...initialState,
    setField(key, value) {
      set(
        (state) => {
          (state as unknown as CheckoutStoreState)[key] = value as never;

          return state;
        },
        false,
        "checkoutStore/setField"
      );
    },
    setFields(values) {
      set(
        (state) => {
          state = {
            ...state,
            ...values,
          };

          return state;
        },
        false,
        "checkoutStore/setFields"
      );
    },

    goToNextStep() {
      set(
        (state) => {
          const nextStep = Math.min(
            state.currentStep + 1,
            CHECKOUT_STEPS.length
          );
          state.currentStep = nextStep;
          state.maxReachedStep = Math.max(state.maxReachedStep, nextStep);
          return state;
        },
        false,
        "checkoutStore/goToNextStep"
      );
      scrollToTop();
    },
    goToPrevStep() {
      set(
        (state) => {
          const prevStep = Math.max(state.currentStep - 1, 1);
          state.currentStep = prevStep;
          return state;
        },
        false,
        "checkoutStore/goToPrevStep"
      );
      scrollToTop();
    },
    goToStep(step) {
      set(
        (state) => {
          const targetStep = Math.max(1, Math.min(step, CHECKOUT_STEPS.length));
          state.currentStep = targetStep;
          state.maxReachedStep = Math.max(state.maxReachedStep, targetStep);
          return state;
        },
        false,
        "checkoutStore/goToStep"
      );
      scrollToTop();
    },
    setCheckoutData(data) {
      set(
        (state) => {
          state.checkoutData = {
            ...(state.checkoutData ?? {}),
            ...data,
          };
          return state;
        },
        false,
        "checkoutStore/setCheckoutData"
      );
    },
    clearCheckoutData() {
      set(
        (state) => {
          state.checkoutData = null;
          return state;
        },
        false,
        "checkoutStore/clearCheckoutData"
      );
    },
    async validateCurrentStep() {
      const currentStep = get().currentStep;
      const validator = stepValidators.get(currentStep);
      
      if (validator) {
        return await validator();
      }

      return true;
    },
    setStepValidator(step, validator) {
      stepValidators.set(step, validator);
    },
    cleanUp() {
      set(initialState, false, {
        type: "checkoutStore/cleanUp",
      });
      stepValidators.clear();
    },
  };
};

export const useCheckoutStore = create<CheckoutStore>(
  middlewares(
    "checkout-store",
    immer(
      persist(createStore, {
        name: "checkout-store",
        storage: createJSONStorage(() => {
          return localStorage;
        }),
      })
    )
  )
);
